<script lang="ts">
    import { run, preventDefault } from 'svelte/legacy';

    import type {
        EmailTemplate,
        PortletDebugInformation,
        SupportEmailDefinition,
    } from 'header/types';
    import { MailingService } from 'portlet/services';

    interface Props {
        definition: SupportEmailDefinition;
    }

    let { definition }: Props = $props();

    let subject = $derived(definition.supportStore.supportEmailData.subject);
    let recipient = $derived(definition.supportStore.supportEmailData.recipient);
    let description = $derived(definition.supportStore.supportEmailData.description);
    let isLoading = $derived(definition.supportStore.loading);
    let error = $derived(definition.supportStore.error);
    let debugInfoRaw = $derived(addNavigationData(definition.debugInfo));
    let debugInfoHTML: string | undefined = $state(undefined);
    run(() => {
        debugInfoHTML = MailingService.convertDebugDataToHTML(debugInfoRaw);
    });
    let buttonTitle = $derived(isLoading ? 'Sending...' : 'Send');

    function handleSubmit() {
        return definition.onSubmit(definition.supportStore.supportEmailData, debugInfoRaw);
    }

    function handleFieldChange(field: keyof EmailTemplate, value: string) {
        definition.onChange({
            ...definition.supportStore.supportEmailData,
            [field]: value,
        });
    }

    function handleChange(event: { currentTarget: HTMLInputElement }) {
        switch (event.currentTarget.id) {
            case formIDs.recipient:
                handleFieldChange('recipient', event.currentTarget.value);
                return;
            case formIDs.subject:
                handleFieldChange('subject', event.currentTarget.value);
                return;
            default:
                return;
        }
    }

    function handleDescriptionChange(event: { currentTarget: HTMLTextAreaElement }) {
        handleFieldChange('description', event.currentTarget.value || '');
    }

    function addNavigationData(debugInfo: PortletDebugInformation) {
        return {
            ...debugInfo,
            userInfo: {
                ...debugInfo.userInfo,
                value: {
                    ...debugInfo.userInfo.value,
                    'browser info': navigator.userAgent || navigator.appVersion,
                    'operating system':
                        (navigator as any).userAgentData?.platform || navigator.platform,
                    url: location.href,
                },
            },
        };
    }

    const formIDs = {
        recipient: 'support-recipient-input',
        subject: 'support-subject-input',
    };
</script>

<form class="form" onsubmit={preventDefault(handleSubmit)}>
    <label for={formIDs.recipient} class="form__item--col1">Recipient</label>
    <input
        value={recipient}
        oninput={handleChange}
        id={formIDs.recipient}
        class="form__item--col2"
        autocomplete="off"
    />

    <label for={formIDs.subject} class="form__item--col1">Subject</label>
    <input
        value={subject}
        oninput={handleChange}
        id={formIDs.subject}
        class="form__item--col2"
        autocomplete="off"
    />

    <textarea
        value={description}
        class="form__description form__item--full-grid"
        oninput={handleDescriptionChange}
    ></textarea>

    <input type="submit" value={buttonTitle} class="form__item--full-grid" />
    {#if error}
        <div class="form__item--full-grid form__item--error">{error}</div>
    {/if}
    <details class="form__item--full-grid">
        <summary>Debug data</summary>
        <div class="form__debug" contenteditable bind:innerHTML={debugInfoHTML}></div>
    </details>
</form>

<style lang="scss" scoped>
    .form {
        display: grid;
        grid-template-columns: 75px 1fr;
        row-gap: 1rem;

        &__item {
            &--full-grid {
                grid-column: 1 / 3;
            }
            &--col1 {
                grid-column: 1 / 2;
            }
            &--col2 {
                grid-column: 2 / 3;
            }

            &--error {
                text-align: center;
                overflow-wrap: break-word;
                color: #dc3e35;
            }
        }

        &__description {
            font-family: inherit;
            font-size: 1em;
            height: 5rem;
        }

        &__debug {
            &:focus-visible {
                outline: none;
            }
        }

        :global(.debug-data) {
            border-collapse: collapse;
            width: 100%;
            :global(td) {
                padding: 0.2rem;
                border: 1px solid #808080;
            }
        }
    }
</style>
